import { handleApiError } from '../utils';
import { MOCK_DATA } from '../constants';

// API 服務類
export class ApiService {
  constructor() {
    this.baseUrl = '/api';
  }

  // 獲取模板列表（內部模板）
  async fetchTemplates() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseUrl}/whatsapptemplates?status=Active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // 正常返回數據（即使是空數組），不應該使用 mock data
          return result.data || [];
        } else {
          // API 返回錯誤，但這是業務邏輯錯誤，不是網絡錯誤，不應該使用 mock data
          console.warn('獲取模板列表 API 返回錯誤:', result.message);
          return [];
        }
      } else {
        // HTTP 錯誤狀態碼，但這是服務器錯誤響應，不是網絡連接失敗
        // 只有在真正的網絡錯誤時才使用 mock data
        console.warn('獲取模板列表 HTTP 錯誤:', response.status, response.statusText);
        return [];
      }
    } catch (error) {
      // 這是真正的網絡錯誤（連接失敗、超時等），可以使用 mock data 作為後備
      console.error('獲取模板列表網絡錯誤（連接失敗）:', error);
      // 只有在網絡連接失敗時才使用 mock data
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.warn('⚠️ 網絡連接失敗，使用模擬數據作為後備');
        return MOCK_DATA.templates;
      }
      // 其他錯誤（如解析錯誤），返回空數組
      return [];
    }
  }

  // Meta「範本資料庫」— 後端 GET WABA message_template_library（Meta 預製庫，非 WABA 既有 message_templates）
  async fetchMessageTemplateLibrary(params = {}) {
    try {
      const token = localStorage.getItem('token');
      const sp = new URLSearchParams();
      if (params.search) sp.append('search', params.search);
      if (params.topic) sp.append('topic', params.topic);
      if (params.usecase) sp.append('usecase', params.usecase);
      if (params.industry) sp.append('industry', params.industry);
      if (params.language) sp.append('language', params.language);
      const q = sp.toString();
      const url = q
        ? `${this.baseUrl}/whatsappmetatemplates/library?${q}`
        : `${this.baseUrl}/whatsappmetatemplates/library`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (response.ok && result.success) {
        return result.data;
      }
      throw new Error(result.message || result.error || 'library fetch failed');
    } catch (e) {
      console.error('fetchMessageTemplateLibrary', e);
      throw e;
    }
  }

  /** 自範本資料庫建立至 WABA，回傳 { id, name, status, ... } */
  async createMetaTemplateFromLibrary(body) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${this.baseUrl}/whatsappmetatemplates/from-library`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    const result = await response.json();
    if (response.ok && result.success) {
      return result.data;
    }
    throw new Error(result.message || result.error || 'from-library failed');
  }

  // 獲取 Meta 模板列表
  async fetchMetaTemplates() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseUrl}/whatsappmetatemplates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // 只返回已審核通過的 Meta 模板
          return (result.data || []).filter(template => template.status === 'APPROVED');
        } else {
          throw new Error(result.message || 'Unknown error');
        }
      } else {
        throw new Error(response.statusText);
      }
    } catch (error) {
      console.error('獲取 Meta 模板列表錯誤:', error);
      return [];
    }
  }

  // 獲取用戶列表
  async fetchUsers() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('🔑 沒有 Token，使用模擬用戶數據');
        return MOCK_DATA.users;
      }

      const response = await fetch(`${this.baseUrl}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const users = await response.json();
        // 過濾出有電話號碼的活躍用戶
        return users.filter(user => user.phone && user.isActive);
      } else {
        throw new Error(response.statusText);
      }
    } catch (error) {
      console.error('獲取用戶列表錯誤:', error);
      return MOCK_DATA.users;
    }
  }

  // 獲取 EForm 列表
  async fetchEForms() {
    try {
      const token = localStorage.getItem('token');
      // pageSize=1000 確保不遺漏任何表單；status=A 由後端過濾（需後端支援）
      const response = await fetch(`${this.baseUrl}/eforms?status=A&pageSize=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        // 確保 data 是數組並過濾啟用的表單
        let forms = Array.isArray(result.data) ? result.data : [];
        // 後端已依 status=A 過濾；前端再防守性過濾一次
        return forms.filter(form => form.status === 'A' || form.status === 'Active');
      } else {
        throw new Error(response.statusText);
      }
    } catch (error) {
      console.error('獲取 EForm 列表錯誤:', error);
      return MOCK_DATA.eforms;
    }
  }

  // 獲取 Commerce Collections 列表（依權限過濾：一般用戶僅自己公司，Tenant_Admin 可選 companyId）
  async fetchCommerceCollections(companyId = null) {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [];
      const url = companyId
        ? `${this.baseUrl}/commerce/collections?companyId=${encodeURIComponent(companyId)}`
        : `${this.baseUrl}/commerce/collections`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('獲取 Commerce Collections 錯誤:', error);
      return [];
    }
  }

  async fetchAiProviders(category = 'AI') {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return [];
      }

      const url = category
        ? `${this.baseUrl}/apiproviders/company?category=${encodeURIComponent(category)}`
        : `${this.baseUrl}/apiproviders/company`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('獲取 AI Provider 失敗:', error);
      return [];
    }
  }

  // 獲取節點類型定義
  async fetchNodeTypeDefinitions() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseUrl}/workflownodetypes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          return result.data;
        }
      } else {
        console.error('獲取節點類型定義失敗:', response.statusText);
        return null;
      }
    } catch (error) {
      console.error('獲取節點類型定義錯誤:', error);
      return null;
    }
  }

  // 獲取流程變量
  async fetchProcessVariables(workflowId) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseUrl}/processvariables/definitions?workflowDefinitionId=${workflowId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          return result.data;
        } else {
          console.log('流程變量載入失敗:', result.message);
          return [];
        }
      } else {
        console.log('流程變量 API 請求失敗:', response.status, response.statusText);
        return [];
      }
    } catch (error) {
      console.error('獲取流程變量失敗:', error);
      return [];
    }
  }

  // 保存流程變量
  async saveProcessVariable(variableData, isUpdate = false, variableId = null) {
    try {
      const token = localStorage.getItem('token');
      const url = isUpdate ? `${this.baseUrl}/processvariables/definitions/${variableId}` : `${this.baseUrl}/processvariables/definitions`;
      const method = isUpdate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(variableData)
      });

      if (!response.ok) {
        // 嘗試把後端錯誤訊息打到 console 方便診斷
        try {
          const bodyText = await response.text();
          console.error(`[apiService.saveProcessVariable] HTTP ${response.status} ${response.statusText} -> ${bodyText}`);
        } catch (_) {
          console.error(`[apiService.saveProcessVariable] HTTP ${response.status} ${response.statusText} (無法讀取 body)`);
        }
        return false;
      }

      return true;
    } catch (error) {
      console.error('保存流程變量失敗:', error);
      return false;
    }
  }

  // 刪除流程變量
  async deleteProcessVariable(variableId) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseUrl}/processvariables/definitions/${variableId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.ok;
    } catch (error) {
      console.error('刪除流程變量失敗:', error);
      return false;
    }
  }

  // 獲取工作流程定義（404 時回傳 null，不拋錯，避免新建或無效 id 時畫面崩潰）
  async fetchWorkflowDefinition(workflowId) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseUrl}/workflowdefinitions/${workflowId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        return await response.json();
      }
      if (response.status === 404) {
        console.warn('流程不存在或無權限:', workflowId);
        return null;
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    } catch (error) {
      console.error('載入流程定義失敗:', error);
      throw error;
    }
  }

  // 保存工作流程定義
  async saveWorkflowDefinition(workflowData, isUpdate = false, workflowId = null) {
    try {
      const token = localStorage.getItem('token');
      const url = isUpdate ? `${this.baseUrl}/workflowdefinitions/${workflowId}` : `${this.baseUrl}/workflowdefinitions`;
      const method = isUpdate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(workflowData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${isUpdate ? '更新失敗' : '創建失敗'}: ${errorText}`);
      }
      
      const data = await response.json().catch(() => null);
      return data;
    } catch (error) {
      console.error('保存工作流程失敗:', error);
      throw error;
    }
  }
}

// 創建單例實例
export const apiService = new ApiService();
